import { useIntl } from "react-intl";
import { Outlet } from "react-router";
import { TextLink } from "@oai/platform/ui/TextLink";

import EmptyPageLayout from "@/components/layout/EmptyPageLayout";
import { useClientErrorBoundaryTelemetry } from "@/lib/clientTelemetry";
import { messages } from "@/localization/Shared.messages";

import type { Route } from "../+types/root";

/**
 * This is the top level app component.
 * It's main purpose, for now, is to act as the applications primary error boundary.
 *
 * We separate this from the root route because the root initializes context that may be used by components
 * inside of the app error boundary.
 *
 */

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const intl = useIntl();
  if (error && error instanceof Error) {
    console.error(error);
  }
  useClientErrorBoundaryTelemetry(error, { boundary: "app" });

  return (
    <EmptyPageLayout>
      <div className="flex w-full grow flex-col items-center justify-center gap-6 px-4 pt-12 text-center">
        <h1 className="text-3xl font-semibold tracking-wide text-neutral-900 uppercase">
          {intl.formatMessage(messages.appErrorTitle)}
        </h1>
        <p className="max-w-[560px] text-lg text-neutral-600">
          {intl.formatMessage(messages.appErrorDescription)}
        </p>
        <TextLink to="/" reloadDocument={true}>
          {intl.formatMessage(messages.goBackHome)}
        </TextLink>
      </div>
    </EmptyPageLayout>
  );
}
